/**
 * TasksNavBadgeService — liefert die offene/überfällige Zahl für die
 * Aufgaben-Nav-Badge (Sidebar + Mobile-Tab, 06-ENTRYPOINT-NAV.md §1).
 *
 * Quelle: die persönliche Aufgaben-Sicht `GET api/Tasks/my` (R2). Gezählt wird mit
 * **derselben Definition wie die Seite „Meine Aufgaben"** ({@link countOverdue}): nur
 * die eigenen Blöcke (CurrentShift + Today + Later, **keine Töpfe** — ein Topf-Eintrag
 * ist noch niemandes Aufgabe), nur offene Aufgaben, und mit „Aufträge nutzen" keine
 * wartenden Schritte. Bis 05.10.2026 zählte das Abzeichen Töpfe und wartende Schritte
 * mit und meldete „4 überfällig", während die Seite „2 überfällig" sagte.
 * Read-only, fail-closed: bei Fehler → 0/0 (kein Badge, 06 S6 „Error → Punkt nutzbar
 * lassen").
 *
 * ⚠️ Bewusst zurückhaltend: NUR laden, wenn das Gate aktiv ist (der Aufrufer ruft
 * {@link refresh}). Im Dark-Launch (Feature aus) wird nie gefetcht.
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map } from 'rxjs/operators';
import { TaskApiService } from '../services/task-api.service';
import { TaskSettingsService } from '../settings/task-settings.service';
import { projectFeatureFlags } from '../settings/task-settings.model';
import { countOverdue } from '../pages/my-tasks.focus';
import { MyTasksDto, TaskDto, TaskStatus } from '../models';
import { TasksNavCounts } from './tasks-nav.logic';

const EMPTY_COUNTS: TasksNavCounts = { openCount: 0, overdueCount: 0 };

@Injectable({ providedIn: 'root' })
export class TasksNavBadgeService {
  private readonly dtoSubject = new BehaviorSubject<MyTasksDto | null>(null);

  /** Läuft gerade ein Abruf? Verhindert Mehrfachanfragen zur selben Antwort. */
  private inFlight = false;

  /**
   * „Aufträge nutzen" — entscheidet wie auf der Seite, ob wartende Schritte zählen.
   * Ändert sich der Schalter nach dem Abruf, rechnen die Zähler ohne neuen Abruf nach.
   */
  private readonly skipWaiting$: Observable<boolean> = this.settings.settings$.pipe(
    map((s) => projectFeatureFlags(s).projectsEnabled),
    distinctUntilChanged()
  );

  /** Aktuelle offene/überfällige Zähler (0/0 bis zum ersten erfolgreichen Fetch). */
  readonly counts$: Observable<TasksNavCounts> = combineLatest([
    this.dtoSubject,
    this.skipWaiting$,
  ]).pipe(map(([dto, skipWaiting]) => countMyTasks(dto, { skipWaiting })));

  /**
   * Die zuletzt geladene Antwort selbst.
   *
   * Flächen, die mehr als die Zähler brauchen (die Aufgaben-Karte der
   * Mitarbeiter-Startseite zeigt die Gruppen), lesen hier mit, statt denselben
   * Endpunkt ein zweites Mal zu rufen.
   */
  readonly myTasks$: Observable<MyTasksDto | null> = this.dtoSubject.asObservable();

  constructor(
    private readonly api: TaskApiService,
    private readonly settings: TaskSettingsService
  ) {}

  /**
   * Lädt Zähler und Antwort neu (nur aufrufen, wenn der Entry-Point aktiv ist).
   * Aktualisiert {@link counts$} und {@link myTasks$}; schluckt Fehler (→ 0/0).
   *
   * **Gleichzeitige Aufrufe werden zu einem.** Hülle und Startseite melden sich
   * beim Betreten derselben Route unabhängig voneinander an; ohne diesen Schutz
   * gingen für dieselbe Antwort zwei Anfragen hinaus (gemessen am 28.08.2026
   * dreimal `GET api/Tasks/my` beim Laden von `/employee/dashboard/heute`).
   */
  refresh(): void {
    if (this.inFlight) {
      return;
    }
    this.inFlight = true;

    this.api
      .getMyTasks()
      .pipe(
        catchError(() => of(null)),
        finalize(() => {
          this.inFlight = false;
        })
      )
      .subscribe((dto) => {
        this.dtoSubject.next(dto ?? null);
      });
  }

  /**
   * P30 (K9): Übernimmt eine Antwort, die eine Seite selbst geladen (oder lokal fortgeschrieben)
   * hat — „Meine Aufgaben" meldet so jeden Stand, damit Heute-Karte, Reiter-Abzeichen und Seite
   * dieselbe Zahl zeigen. Kein eigener Abruf.
   */
  publish(dto: MyTasksDto | null): void {
    this.dtoSubject.next(dto ?? null);
  }

  /** Setzt die Zähler zurück (z. B. bei Logout / Gate versteckt). */
  reset(): void {
    this.dtoSubject.next(null);
  }
}

/**
 * Zählt offene/überfällige Aufgaben aus der MyTasksDto-Struktur (pure, testbar).
 *
 * `overdueCount` ist **genau** {@link countOverdue} der Seite „Meine Aufgaben" — keine
 * eigene Auslegung, sonst laufen Abzeichen und Seite auseinander. `openCount` zählt
 * dieselben Blöcke (ohne Töpfe), nur offene Aufgaben (`Status = Open`, wie die Seite) —
 * nicht erledigt, storniert, verpasst oder zur Abnahme gemeldet.
 */
export function countMyTasks(
  dto: MyTasksDto | null | undefined,
  opts: { skipWaiting?: boolean } = {}
): TasksNavCounts {
  if (!dto) return { ...EMPTY_COUNTS };
  const own: TaskDto[] = [...(dto.CurrentShift ?? []), ...(dto.Today ?? []), ...(dto.Later ?? [])];
  const openCount = own.filter(isStillMine).length;
  const overdueCount = countOverdue(dto, opts);
  return { openCount, overdueCount };
}

/**
 * P30 (K9): **dieselbe Regel wie die Seite** (`openCount` in `my-tasks-page` und
 * `my-tasks-mobile.page`: `Status === Open`). Bis 07.10.2026 zählte das Abzeichen auch
 * **Verpasst** als „offen" — die Heute-Karte konnte damit nie kleiner, aber größer als die
 * Seite sein.
 */
function isStillMine(task: TaskDto): boolean {
  return task.Status === TaskStatus.Open;
}
